import type { components } from '../../lib/api/schema.gen'
import type { RfqDraftService } from '../../lib/rfq/rfq-bundle-context'
import { addAmounts, centsFromAmount, multiplyAmount } from '../cart/cart-money'
import { formatProcessLabel } from '../parts/process-label'
import type { QuoteVariant } from './domain'
import { applyVariantRatio } from './variant-pricing'

type Sourceable = components['schemas']['Sourceable']
type SourceableRef = components['schemas']['SourceableRef']
type Price = components['schemas']['Price']
type PricingProjection = components['schemas']['PricingProjection']
type LineItem = components['schemas']['LineItem']
type RfqCreateRequest = components['schemas']['RfqCreateRequest']
type RfqUpdateRequest = components['schemas']['RfqUpdateRequest']
type RfqLineRequest = components['schemas']['RfqLineRequest']
type RfqLineAppendRequest = components['schemas']['RfqLineAppendRequest']
type RfqRegion = components['schemas']['RfqRegion']
type RfqRequestedService = components['schemas']['RfqRequestedService']
type RfqShipMethod = components['schemas']['RfqShipMethod']

/** UID-260 gap #1/#2/#4 follow-up — `SourceableRef` PLUS the two OPTIONAL
 * pricing-preview fields a full `Sourceable` carries but a bare
 * `SourceableRef` does not. Every caller that adds a part to the RFQ bundle
 * (`toReviewLine` below, and the Library/BOM/PartDetail `rfqBundle.addItems`
 * call sites feeding `RfqReviewPage`'s `lines` derivation) already HOLDS a
 * full `Sourceable`/`LibrarySourceable` at that moment — this just widens the
 * TYPE so those already-present fields survive onto `RfqReviewLine` instead
 * of being narrowed away, so the pre-quote price-range/volume-pricing
 * preview below has real data to read. No new fetch anywhere. */
export type ReviewLineSourceable = SourceableRef & {
  readonly indicativePrice?: Price
  readonly pricingProjection?: PricingProjection
  /** UID-335 follow-up — same widening rationale as the two fields above:
   * the full `Sourceable` a caller holds at add-time already carries
   * `imageRef` (a real catalog stock photo, or — once `add-parts-search.ts`
   * merges it in — a generated CAD thumbnail for a custom/uploaded part).
   * Threaded through so `PartsReviewTable` has something real to render
   * instead of its decorative empty placeholder. */
  readonly imageRef?: string | null
  /**
   * UID-604 / ADR-0177 (third rung added by UID-605) — SAME widening rationale as
   * the three fields above: the full `Sourceable` a caller holds at add-time
   * already carries `name`, `catalogSku` and `customerBomId`, and `RfqReviewPage`
   * already passes that whole object through (`sourceable: item.sourceable`);
   * this type was merely narrowing them away.
   *
   * These are the RAW, un-synthesized strings, and they are exactly the ones the
   * SERVER's `linePartIdentity` snapshot is built from
   * (`part-requirements-lookup.ts` resolves `part.sourceable.name`,
   * `part.sourceable.catalogSku` and `part.sourceable.customerBomId`). That is
   * what lets `RfqLineConfiguratorModal` seed the live estimate through the
   * shared `configurationPartSeed` ladder and have the footer's figure AGREE
   * with the one the save path recomputes (ADR-0177 §4, as amended by UID-605).
   *
   * DECLARED ON PURPOSE (ADR-0177 Amendment A2). Before UID-605 these survived
   * on most paths only as an untyped structural passthrough — real at runtime,
   * invisible to the type system, pinned by no test. That is not agreement, it
   * is luck: `toReviewLine` below is the one hop that reconstructs `sourceable`
   * field-by-field, and it dropped them, so a part added through
   * `RfqReviewPage`'s "Add parts" modal priced differently in the footer than in
   * the saved RFQ. A surface feeding that footer must carry this identity in its
   * TYPE.
   *
   * Deliberately NOT the same thing as {@link RfqReviewLine.name}, which is
   * `sourceable.name ?? 'Item <first 8 chars of id>'` — a client-INVENTED label
   * for a nameless part, and forbidden as a pricing seed for that reason
   * (Amendment A6: `name` is the label, `sourceable.name` is the identity).
   */
  readonly name?: string
  readonly catalogSku?: string | null
  /** The customer's own BOM id — the seed ladder's THIRD rung, for a
   * BOM-sourced part carrying neither a library name nor a catalog SKU. The
   * server has always had this rung (`boundedOrNull(catalogSku) ??
   * boundedOrNull(customerBomId)`); the client had none until UID-605, so such
   * a part seeded server-side and not client-side. */
  readonly customerBomId?: string | null
}
/** UID-260 (ADR-0085) — one draft line's Configure-step output (typed
 * attributes + server-derived, possibly-still-pending price). Re-exported
 * here so callers (`PartsReviewTable`, `RfqReviewPage`) can import it
 * alongside `RfqReviewLine` instead of reaching into `schema.gen` directly. */
export type SpecSelection = components['schemas']['SpecSelection']
/** UID-346 (ADR-0117 D4/D5) — the typed configuration that replaced the untyped
 * `attributes` bag as the source of truth for "configured". */
export type RfqLineSpecification = components['schemas']['RfqLineSpecification']

/**
 * UID-56 RFQ review screen (screens.md §15) — pure, framework-free helpers
 * for the build/review form's local state. Kept separate from
 * `RfqReviewPage.tsx` so the counting/validation/wire-mapping logic the
 * verification checklist asks for is unit-testable without mounting React.
 */

/** One row in the review screen's parts table — a local, editable draft of
 * a `LineItem` (quantity is a plain number here; `buildCreateRequest`
 * re-wraps it into the contract's `Quantity` value object on submit). */
export interface RfqReviewLine {
  readonly id: string
  readonly sourceable: ReviewLineSourceable
  readonly name: string
  readonly sku: string | null
  readonly quantity: number
  /** UID-260 (ADR-0085) — this line's Configure-step output, or `null` while it
   * still "needs Configure" (the gating signal `PartsReviewTable`'s per-row/bulk
   * Configure action and the submit CTA both read). Mirrors `RfqLineView.specSelection`
   * (server read shape) — `null`/absent both mean "not yet configured." */
  readonly specSelection: SpecSelection | null
}

/** One add-on service the customer is building up before submit. Carries a
 * `localId` (React key / remove target) that never reaches the wire —
 * `buildCreateRequest` drops it when mapping onto `RfqRequestedService`.
 *
 * ADR-0049: this is now an alias of `RfqDraftService`, defined in `lib/`
 * (schema.gen primitives + `localId`) so the compose-screen store
 * (`RfqBundleValue.draft.services`) can hold it without `lib/` importing
 * this vertical (verticals depend on lib, never the reverse). Structurally
 * unchanged — same fields, same runtime shape. */
export type RfqReviewService = RfqDraftService

/** The targeting card's region radio list (screens.md §15, ADR-0070): a
 * 3-option US SOURCING-PREFERENCE picker — design-literal values + labels,
 * in the design's order — REPLACING the retired 4-country
 * country-of-manufacture picker plus its client-only `'no_preference'`
 * sentinel. `any` ("No preference") is now a real, default-selected enum
 * member; there is no longer an absent/omitted-from-wire "no preference"
 * state (ADR-0042's omit-from-wire convention is retired by ADR-0070). */
export const REGION_OPTIONS: readonly {
  readonly value: RfqRegion
  readonly label: string
}[] = [
  { value: 'usa', label: 'Must be made in USA' },
  { value: 'prefer_usa', label: 'Prefer USA, open to others' },
  { value: 'any', label: 'No preference' },
]

/** The shipping card's method `<select>` options (UID-201, ADR-0076 — refines the
 * UID-117/ADR-0053 radio group into the CHECKOUT §2 control pattern applied to RFQ
 * compose). Design-literal labels with lead-time copy from the checkout §2 ship-method
 * rows (`Fictiv Platform.dc.html` L2012-2013: "Ground (5 business days)" / "Overnight
 * (next business day)") and the own-account card ("Use my own shipping account").
 *
 * The leading `'none'` entry is a NON-VALUE PLACEHOLDER `<option>` ("Select a shipping
 * method"), NOT a wire enum member: it maps to OMITTING `shipMethod` from the request
 * (`buildCreateRequest` drops `'none'`). ADR-0076 Decision 3 keeps this omit-from-wire
 * capability (deliberately DIFFERENT from UID-199 region, which promoted its default to a
 * real member) — "the customer didn't pick a shipping method" is a meaningful, distinct
 * absent state. `RfqShipMethod` (ground | overnight | own_account) is UNCHANGED. */
export const SHIP_METHOD_OPTIONS: readonly {
  readonly value: RfqShipMethod | 'none'
  readonly label: string
}[] = [
  { value: 'none', label: 'Select a shipping method' },
  { value: 'ground', label: 'Ground (5 business days)' },
  { value: 'overnight', label: 'Overnight (next business day)' },
  { value: 'own_account', label: 'Use my own shipping account' },
]

/** The add-on service form's type select — the independent, app-owned
 * requestable-manufacturing-service vocabulary (ADR-0071), decoupled from
 * ChargeKind and aligned to the design's add-on services catalog. Labels and
 * subtitles are CLIENT-DERIVED (never on the wire). Subtitle is omitted for
 * types the design carries no copy for (certification/expedite). */
export const SERVICE_TYPE_OPTIONS: readonly {
  readonly value: RfqRequestedService['type']
  readonly label: string
  readonly subtitle?: string
}[] = [
  { value: 'assembly', label: 'Assembly', subtitle: 'Mechanical / sub-assembly build' },
  { value: 'inserts', label: 'Threaded inserts', subtitle: 'Heat-set / press-fit inserts' },
  { value: 'cleanroom', label: 'Clean-room packaging', subtitle: 'ISO-class pack-out' },
  { value: 'finishing', label: 'Finishing & coating', subtitle: 'Anodize, paint, plating' },
  {
    value: 'inspection',
    label: 'Inspection & metrology',
    subtitle: 'CMM / FAIR / source inspection',
  },
  { value: 'certification', label: 'Certification' },
  { value: 'expedite', label: 'Expedite' },
  { value: 'custom', label: 'Custom service', subtitle: 'Describe what you need' },
]

/** Builds a review line from a fixture Sourceable (the "Add parts" picker's
 * source), defaulting quantity to 1 sale-unit.
 *
 * `sessionAccountId` — the signed-in tenant — is stamped onto every
 * tenant-owned (non-catalog) Sourceable, REPLACING whatever accountId the
 * fixture carries: the demo fixtures belong to the fictional Apex Automation
 * tenant, but the server's guards reject any Sourceable whose accountId is
 * not the session tenant (403 on cart adds, ADR-0002). Catalog parts stay
 * global (no accountId), per the shared Sourceable invariant. */
export function toReviewLine(sourceable: Sourceable, sessionAccountId: string): RfqReviewLine {
  return {
    id: sourceable.id,
    sourceable: {
      id: sourceable.id,
      kind: sourceable.kind,
      uom: sourceable.uom,
      ...(sourceable.kind === 'catalog' ? {} : { accountId: sessionAccountId }),
      // UID-260 gap #1/#2/#4 follow-up — thread the pricing-preview fields
      // already present on the fetched `Sourceable` through onto the review
      // line's (widened) `ReviewLineSourceable`, instead of narrowing them
      // away as before. See `ReviewLineSourceable`'s doc comment.
      ...(sourceable.indicativePrice ? { indicativePrice: sourceable.indicativePrice } : {}),
      ...(sourceable.pricingProjection ? { pricingProjection: sourceable.pricingProjection } : {}),
      ...(sourceable.imageRef ? { imageRef: sourceable.imageRef } : {}),
      // UID-605 (ADR-0177 Amendment A2) — THREAD THE RAW IDENTITY. This is the
      // hop the bug actually lived in: every OTHER call site that queues a part
      // spreads a full `Sourceable` into the bundle, so the footer's per-part
      // seed survived there structurally; this function reconstructs `sourceable`
      // field-by-field and used to omit `name`/`catalogSku`/`customerBomId`. A
      // part added through `RfqReviewPage`'s "Add parts" modal
      // (`handleAddParts` → here) therefore lost its identity, and the footer
      // priced at the un-seeded base while the save path priced off the server's
      // real snapshot — the same part at two prices.
      //
      // The previous note here argued these must be omitted because
      // `buildLineRequest` maps `line.sourceable` STRAIGHT ONTO THE WIRE. The
      // premise is true; the conclusion was wrong, and it cost a defect. The
      // frozen `SourceableRef`/`LineItem` schemas carry no
      // `additionalProperties: false` and the app runs `removeAdditional: false`,
      // so extra keys are ACCEPTED and then dropped by `sanitizeLineItems`'
      // `LINE_ITEM_SPEC` whitelist before anything is persisted (`domain.ts`;
      // ADR-0166). They are inert on the wire — which is already demonstrated,
      // because the six other queuing call sites have been sending them all
      // along. The server still derives a line's identity from its own
      // tenant-scoped parts-library read and NEVER from the request body
      // (ADR-0063 §4, UID-530); nothing here is trusted for persistence. These
      // exist for ONE local consumer: the configurator footer's pricing seed.
      ...(sourceable.name ? { name: sourceable.name } : {}),
      ...(sourceable.catalogSku ? { catalogSku: sourceable.catalogSku } : {}),
      ...(sourceable.customerBomId ? { customerBomId: sourceable.customerBomId } : {}),
    },
    // ADR-0177 §4 / Amendment A6 — a DISPLAY LABEL, and FORBIDDEN as a pricing
    // seed: for a nameless part this invents `Item <8 hex of id>`, a string the
    // server has never seen, so seeding from it would guarantee footer-vs-saved
    // disagreement exactly on the lines where identity is weakest. The identity
    // is `sourceable.name` above; that is why both live on this object.
    name: sourceable.name ?? `Item ${sourceable.id.slice(0, 8)}`,
    sku: sourceable.catalogSku ?? null,
    quantity: 1,
    // A freshly-added part has never been through Configure — the real value (once
    // set) lives in `RfqBundleValue.draft.specSelections`, keyed by sourceable id
    // (see `RfqReviewPage`'s `lines` derivation), not here.
    specSelection: null,
  }
}

// ---------------------------------------------------------------------------
// UID-260 gap #5 (real bug fix) + gap #1/#2/#4 follow-up (frontend-only,
// preview-only) — see this ticket's report for the full rationale. Pure,
// framework-free helpers so the Configure-gating fix and the pricing-preview
// math are unit-testable without mounting `PartsReviewTable`/`RfqReviewPage`.
// ---------------------------------------------------------------------------

/** A catalog line is already priced from the catalog and never carries a
 * `specSelection` — there is nothing for the Configure workflow to write for
 * it. The sole predicate both `needsConfigure` (gap #5) and the per-row
 * Configure/teal-add-to-cart branch (`PartsReviewTable`) read. */
export function isCatalogLine(line: RfqReviewLine): boolean {
  return line.sourceable.kind === 'catalog'
}

/** UID-260 gap #5 — the REAL fix: a catalog line is never "needs Configure"
 * (see `isCatalogLine`'s doc comment). Before this fix, `needsConfigureLineIds`
 * only checked `!line.specSelection` — always true for a catalog line, since
 * catalog `Sourceable`s never get one — so any RFQ containing a catalog part
 * permanently showed "needs Configure" and blocked submission, even though
 * there was nothing left to configure.
 *
 * UID-346 (ADR-0117 D5) — SECOND real fix, layered on top of the first: this
 * predicate used to truthiness-check the WRAPPER (`!line.specSelection`), not
 * its content. Under the pre-UID-346 contract `SpecSelection.attributes` was an
 * unconstrained `Record<string, string>` bag with no `minProperties`, so
 * `{attributes: {}}` was a perfectly valid, truthy `SpecSelection` — a Save with
 * every field left blank produced exactly that shape (the old `ConfigurePartsModal`
 * only ever copied non-blank rows), which satisfied `!!line.specSelection` and
 * silently marked the line configured. That let a spec-less RFQ decrement
 * `needsConfigCount` to zero and reach Submit with nothing actually specified.
 *
 * UID-346's contract closes this by construction (D4/D5): `RfqLineSpecification`
 * requires `process`/`material`/`finish`, so an empty specification is no longer
 * REPRESENTABLE at all — there is no `{}` equivalent to be truthy-but-empty.
 * Reading `line.specSelection?.specification` (rather than the wrapper) is what
 * makes that guarantee actually reach this predicate: a legacy, bag-only row from
 * before this ticket (whose `specification` cannot be synthesised from `attributes`,
 * per ADR-0117 D5's "legacy data posture") correctly reads back as `specification ==
 * null` — i.e. still "needs Configure" — rather than as configured. That is the
 * intended outcome, not a regression: those are exactly the under-specified
 * selections this decision exists to stop reaching Submit. */
export function needsConfigure(line: RfqReviewLine): boolean {
  return !isCatalogLine(line) && !line.specSelection?.specification
}

/** The submit-gating id list `RfqReviewPage` reads for `needsConfigCount`,
 * the summary rail's "Configure" CTA target, and (indirectly, via the same
 * per-line `needsConfigure` predicate) the bulk toolbar's "Configure N". */
export function needsConfigureLineIds(lines: readonly RfqReviewLine[]): readonly string[] {
  return lines.filter(needsConfigure).map((line) => line.id)
}

/** UID-434/WS-D follow-up — the "Configure next part" CTA's targeting rule
 * (`RfqLineConfiguratorModal`/`RfqReviewPage`). Single-sourced off the SAME
 * `needsConfigureLineIds` predicate this file already pins (rather than
 * reimplementing the dc.html `rfqNextToConfig` loop a second time) — but,
 * per Gate 5 W7, the ORDERING is a genuine forward traversal, not "the first
 * entry of that predicate's output".
 *
 * Walks `lines` FORWARD starting just after `excludeLineId`'s own position,
 * wrapping around to the front once it runs off the end, and returns the
 * first line encountered (other than `excludeLineId` itself) that still
 * needs Configure. Before this fix, `needsConfigureLineIds(lines).find(id =>
 * id !== excludeLineId)` answered "the first OTHER line in `lines` order
 * that needs Configure" — which, for e.g. `[A, B, C]` all unconfigured with
 * C currently open, jumped BACKWARD to A instead of forward (with wrap) to
 * A only because there is nothing after C — but for `[A, B, C]` with B open,
 * it also answered A, skipping over C and landing behind the buyer instead
 * of ahead of them. "Configure next part" should feel like moving forward
 * through the draft, wrapping to the top once you reach the end — never
 * jumping backward past lines you haven't reached yet.
 *
 * Returns `null` when no OTHER line needs it — including when
 * `excludeLineId` is itself the only entry in `needsConfigureLineIds`, or
 * isn't found in `lines` at all (defensive; falls back to the unordered
 * "first other" answer in that case, since there is no position to walk
 * forward from). */
export function nextNeedsConfigureLineId(
  lines: readonly RfqReviewLine[],
  excludeLineId: string,
): string | null {
  const needsConfigureIds = new Set(needsConfigureLineIds(lines))
  if (needsConfigureIds.size === 0) return null

  const excludeIndex = lines.findIndex((candidate) => candidate.id === excludeLineId)
  if (excludeIndex === -1) {
    return [...needsConfigureIds].find((id) => id !== excludeLineId) ?? null
  }

  for (let offset = 1; offset <= lines.length; offset += 1) {
    const candidate = lines[(excludeIndex + offset) % lines.length]
    if (candidate && candidate.id !== excludeLineId && needsConfigureIds.has(candidate.id)) {
      return candidate.id
    }
  }
  return null
}

/**
 * UID-263 (ADR-0087 D1) — the bulk "Add N priced to cart" button's set: the
 * IDENTICAL predicate the per-row `CatalogAddToCartButton` uses to decide
 * whether to render (`isCatalogLine`, `PartsReviewTable.tsx:256`). Bound to
 * this shared predicate — never to the header `pricedCount`
 * (`specSelection.unitPriceAmount`, a disjoint population of
 * configured-custom lines, ADR-0087 D1) — so the bulk label's integer can
 * never drift from the number of visible per-row `+` buttons on screen.
 * Do NOT widen this to include configured-custom rows: their
 * `unitPriceAmount` is server-forced `null` pre-quote (ADR-0085 D1), so
 * there is no resolved price for the bulk action to add.
 */
export function cartEligibleLines(lines: readonly RfqReviewLine[]): readonly RfqReviewLine[] {
  return lines.filter(isCatalogLine)
}

/** The id list for `cartEligibleLines` — the bulk-add loop's target set. */
export function cartEligibleLineIds(lines: readonly RfqReviewLine[]): readonly string[] {
  return cartEligibleLines(lines).map((line) => line.id)
}

/** Gap #1/#2/#4's candidate population: a NON-catalog line whose `Sourceable`
 * is `auto_priced` AND carries a real `indicativePrice`. Deliberately
 * excludes:
 *  - `catalog` lines — flat, region-independent price (no range/preview);
 *  - `quote_required` custom lines — `specSelection.unitPriceAmount` is
 *    always server-forced `null` pre-quote (ADR-0085 D1), so there is no
 *    real base price to scale; these keep showing "Needs quote"/Configure
 *    exactly as before this ticket.
 */
export function isAutoPricedCustomCandidate(line: RfqReviewLine): boolean {
  return (
    !isCatalogLine(line) &&
    line.sourceable.pricingProjection?.mode === 'auto_priced' &&
    Boolean(line.sourceable.indicativePrice)
  )
}

/** Every gap #1/#2/#4 candidate line in `lines`, in the given order. */
export function autoPricedCustomCandidates(
  lines: readonly RfqReviewLine[],
): readonly RfqReviewLine[] {
  return lines.filter(isAutoPricedCustomCandidate)
}

/**
 * UID-401 TASK A — the ONE "is this line priced?" predicate, built once so it
 * can never disagree with itself across the four surfaces that need it
 * (`PartsReviewTable`'s `UnitPriceCell`, `RfqPreQuotePricingPreview`'s gate,
 * `previewQuoteOptionTotals`'s candidate filter, and the summary rail's
 * Checkout CTA gate). Mirrors the design's `allPriced` computation
 * (`dc.html:~9680`, `rowsData.length>0 && unpricedCount===0`) at the
 * per-line grain.
 *
 * A line genuinely has a price to show iff:
 *  1. it carries a REAL, server-derived price (`specSelection.unitPriceAmount`) — a
 *     real backend price always counts; OR
 *  2. it is a catalog line with a real `indicativePrice` (flat,
 *     region-independent, nothing to Configure); OR
 *  3. it is an auto-priced custom candidate (`isAutoPricedCustomCandidate`)
 *     that does NOT still need configuration (`needsConfigure`) — an
 *     auto-priced line whose Configure step has never run is a PREVIEW
 *     estimate attached to a row that isn't actually ready yet (TASK B),
 *     not a priced line.
 *
 * CRITICAL: `UnitPriceCell` (`PartsReviewTable.tsx`) does NOT re-derive this
 * test — its branch chain is ordered so that exactly this predicate decides
 * whether a price/preview renders (`needsConfigure` first, then real price,
 * then catalog price, then the candidate preview). See
 * `PartsReviewTable.test.tsx`'s "isLinePriced ⇔ UnitPriceCell renders a
 * price" pin for the correspondence test this constraint requires. Do not
 * add a fourth "priced" path to either place without updating both — that is
 * exactly the two-surfaces-drift hazard ADR-0124 (`staging-cta.ts` vs
 * `buildStagingAnalysisSignal`) and ADR-0087 D1 (bulk-label drift) were
 * both burned by.
 */
export function isLinePriced(line: RfqReviewLine): boolean {
  // Gate-5 C3 (UID-401) — `needsConfigure` is hoisted to the FRONT, structurally
  // mirroring `UnitPriceCell`'s branch chain, which tests it FIRST for every
  // line shape (ADR-0118 D3 made that ordering binding).
  //
  // It used to be conjoined to the third disjunct ONLY, which made this
  // predicate's own doc comment ("its branch ordering IS that predicate") false
  // and re-opened the exact `$0.00` card D3 exists to prevent. The counterexample
  // is representable and not hypothetical: a NON-catalog line carrying
  // `specSelection.unitPriceAmount` but NO `specSelection.specification` — see
  // `needsConfigure`'s doc comment on legacy attributes-bag rows, which read back
  // `specification == null` yet acquire a `unitPriceAmount` once quoted.
  //
  // For that line `needsConfigure` is true, so `UnitPriceCell` renders the
  // Configure button and NO price, while the old branch 1 answered "priced".
  // Three things broke at once: Checkout/"Generate quote" appeared on a draft
  // whose Submit was blocked by that same predicate; the D3 double gate passed
  // (a candidate exists AND `allLinesPriced`) while `previewQuoteOptionTotals`
  // filtered the line out on `needsConfigure`, summing every variant to `0.00`
  // and rendering "Choose a quote option" at **$0.00 /ea** — the placeholder
  // money ADR-0100 D1 forbids by name; and `cartEligibleLines` (catalog-only)
  // could not move it to the cart either.
  //
  // Hoisting the guard closes all three. Do NOT push it back inside a disjunct:
  // "unconfigured ⇒ never priced" must hold for EVERY branch, not one.
  if (needsConfigure(line)) return false
  return (
    Boolean(line.specSelection?.unitPriceAmount) ||
    (isCatalogLine(line) && Boolean(line.sourceable.indicativePrice)) ||
    isAutoPricedCustomCandidate(line)
  )
}

/**
 * UID-401 TASK A — the design's `allPriced` (`dc.html:~9680`), lifted to the
 * per-line `isLinePriced` predicate above: `true` only when there is at
 * least one line AND every line is priced. An empty draft is never
 * "all priced" — there is nothing to check out.
 */
export function allLinesPriced(lines: readonly RfqReviewLine[]): boolean {
  return lines.length > 0 && lines.every(isLinePriced)
}

/** Gap #1's per-row PREVIEW price range, shown before the customer has
 * picked a quote option: `[cheapest variant, priciest variant]` applied to
 * the line's `indicativePrice`, via the SAME money-safe `applyVariantRatio`
 * the real post-quote `QuoteVariantSelector` uses (BigInt/decimal — never a
 * float multiply). `undefined` when `line` isn't a real candidate (see
 * `isAutoPricedCustomCandidate`) — the caller renders nothing extra in that
 * case. PREVIEW-ONLY: this is a client-side estimate, not a real Quote
 * (ADR-0051) — see `RfqPreQuotePricingPreview.tsx`'s doc comment for the
 * full caveat this ticket requires surfacing in the UI. */
export function previewPriceRange(
  line: RfqReviewLine,
  variants: readonly QuoteVariant[],
): { readonly lo: string; readonly hi: string; readonly currency: string } | undefined {
  const price = line.sourceable.indicativePrice
  if (!isAutoPricedCustomCandidate(line) || !price || variants.length === 0) return undefined
  const amounts = variants.map((variant) => applyVariantRatio(price.amount, variant.ratio))
  const lo = amounts.reduce((min, amount) => (Number(amount) < Number(min) ? amount : min))
  const hi = amounts.reduce((max, amount) => (Number(amount) > Number(max) ? amount : max))
  return { lo, hi, currency: price.currency }
}

/** Gap #1's single PREVIEW price once the customer has picked a quote
 * option: `indicativePrice × selectedVariant.ratio`. `undefined` when `line`
 * isn't a real candidate. PREVIEW-ONLY — see `previewPriceRange`'s doc
 * comment. */
export function previewPriceForVariant(
  line: RfqReviewLine,
  variant: QuoteVariant,
): { readonly amount: string; readonly currency: string } | undefined {
  const price = line.sourceable.indicativePrice
  if (!isAutoPricedCustomCandidate(line) || !price) return undefined
  return { amount: applyVariantRatio(price.amount, variant.ratio), currency: price.currency }
}

/** Gap #2's "Choose a quote option" PREVIEW selector's per-variant preview
 * TOTAL — mirrors `RfqCard.tsx`'s real post-quote `previewAmounts`
 * derivation exactly (the QA/UID-116 defect precedent this ticket must not
 * regress): each candidate line's amount is rounded INDIVIDUALLY per
 * variant via `applyVariantRatio`/`multiplyAmount`, then summed via
 * `addAmounts` — never the ratio applied once to an aggregate base total
 * (a different, wrong number; see `RfqCard.tsx`'s own comment on this). */
export function previewQuoteOptionTotals(
  lines: readonly RfqReviewLine[],
  variants: readonly QuoteVariant[],
): Record<QuoteVariant['id'], string> {
  // UID-401 TASK C — an unconfigured candidate (still `needsConfigure`) no
  // longer contributes to the preview total. Before this ticket, a line
  // that still needed Configure could inflate this total with a price the
  // customer had no way to act on yet (TASK B: an unconfigured line must
  // never show pricing anywhere, and this total is exactly that — a sum of
  // per-line prices). Filtering with the same `needsConfigure` predicate
  // `isLinePriced` reads keeps this total's candidate population identical
  // to the priced-candidate population `isLinePriced` recognizes.
  const candidates = autoPricedCustomCandidates(lines).filter((line) => !needsConfigure(line))
  return Object.fromEntries(
    variants.map((variant) => [
      variant.id,
      candidates.length > 0
        ? addAmounts(
            candidates.map((line) => {
              // Guarded by `autoPricedCustomCandidates` above — `indicativePrice`
              // is always present here; the fallback is unreachable defensive code.
              const price = line.sourceable.indicativePrice
              const amount = price ? applyVariantRatio(price.amount, variant.ratio) : '0.00'
              return multiplyAmount(amount, line.quantity)
            }),
          )
        : '0.00',
    ]),
  ) as Record<QuoteVariant['id'], string>
}

/**
 * UID-309 (reference parity) — the Request Summary rail's "Part production"
 * line: the sum of every CATALOG line's `indicativePrice × quantity` (the
 * same real, flat, region-independent price `cartEligibleLines`/the bulk
 * "Add to cart" action already uses). Deliberately does NOT include custom
 * lines — a `quote_required` custom line's `specSelection.unitPriceAmount`
 * is server-forced `null` pre-quote (ADR-0085 D1), and an
 * `isAutoPricedCustomCandidate`'s ratio-applied amount is a PREVIEW tied to
 * a specific quote-option selection (`previewPriceForVariant`), not a
 * committed price to sum into a running subtotal — including either would
 * cross the money-path-integrity boundary (ADR-0004/0051/UID-126) this
 * function is deliberately scoped to stay clear of. Custom lines simply
 * contribute nothing; `0.00` when there are no priced catalog lines at all
 * (an all-custom draft), matching the reference's own "$0.00, still
 * calculating" treatment before anything is priced.
 *
 * MIXED CURRENCY → `null` (UID-309 Gate 5 code review, 2026-08-25). `Price.currency`
 * is an unconstrained `string` on the wire (not a USD-only enum), and `addAmounts`
 * is currency-BLIND — it reduces integer cents with no currency check. Summing
 * across currencies would therefore silently produce a meaningless number labelled
 * with whichever line happened to come first. This mirrors the deliberate,
 * tested guard in this vertical's sibling `domain.subtotalForQuote`
 * (`domain.ts:641-642`, `domain.test.ts:778`) rather than inventing a second
 * convention — the ADR-0095 G9 / UID-302 precedent is that a display subtotal
 * diverging from its siblings is a REAL defect, not a cosmetic one. Refusing to
 * compute is the money-path-integrity-preserving answer (ADR-0004/0051/UID-126):
 * the caller renders `N/A`, exactly as `OpenQuotes.tsx:251` already does for the
 * sibling's `null`. NOTE the deliberate asymmetry with the sibling: "no priced
 * lines at all" stays `0.00` here (documented above) rather than `null` — only the
 * genuinely-uncomputable mixed-currency case is `null`.
 */
export function partProductionSubtotal(lines: readonly RfqReviewLine[]): {
  readonly amount: string
  readonly currency: string
} | null {
  const priced = pricedCatalogLines(lines)
  const currency = priced[0]?.price.currency ?? 'USD'
  if (priced.some(({ price }) => price.currency !== currency)) return null
  const amount = addAmounts(
    priced.map(({ line, price }) => multiplyAmount(price.amount, line.quantity)),
  )
  return { amount, currency }
}

/** Gate 5 code review (S12) — `centsFromAmount` (`cart-money.ts`) throws BY
 * DESIGN on a malformed decimal-string amount, so every catalog line's price
 * routing through `partProductionSubtotal` on every `/rfq/new` render meant
 * one malformed server price would white-screen the whole compose screen.
 * The shared candidate set both `partProductionSubtotal` and
 * `partProductionLineCount` (below) read from — a line whose price fails to
 * parse is treated the SAME as "no price yet": silently non-contributing,
 * never a thrown exception during render. Mirrors `subtotalForQuote`'s
 * sibling posture of refusing to compute rather than crashing or fabricating
 * a number. */
function pricedCatalogLines(
  lines: readonly RfqReviewLine[],
): readonly { readonly line: RfqReviewLine; readonly price: Price }[] {
  return cartEligibleLines(lines).flatMap((line) => {
    const price = line.sourceable.indicativePrice
    if (!price) return []
    try {
      centsFromAmount(price.amount)
    } catch {
      return []
    }
    return [{ line, price }]
  })
}

/** Gate 5 code review (S4) — the Request Summary rail's "Part production
 * (N parts)" label must count exactly the lines `partProductionSubtotal`
 * actually summed, NOT `cartEligibleLines(lines).length` (`isCatalogLine`
 * alone, no price requirement) — that count silently includes an unpriced
 * (or malformed-price, S12) catalog line as a non-contributor, so the label
 * and the total it's supposed to describe disagreed (e.g. "Part production
 * (2 parts) — $10.00" with only 1 line actually priced). Deliberately
 * independent of `partProductionSubtotal`'s own mixed-currency `null`: the
 * count is still meaningful (it's the population that WOULD have summed)
 * even when the cross-currency total itself is uncomputable. */
export function partProductionLineCount(lines: readonly RfqReviewLine[]): number {
  return pricedCatalogLines(lines).length
}

export interface RequestSummary {
  readonly partsCount: number
  readonly unitsCount: number
  readonly servicesCount: number
}

/** The request-summary rail's three counts (screens.md §15). `partsCount`
 * is distinct LINES, not units — a 5-unit line and a 1-unit line are both
 * "1 part." */
export function summarizeRequest(
  lines: readonly RfqReviewLine[],
  services: readonly RfqReviewService[],
): RequestSummary {
  return {
    partsCount: lines.length,
    unitsCount: lines.reduce((sum, current) => sum + current.quantity, 0),
    servicesCount: services.length,
  }
}

/** The default subtitle when nothing has been filled in yet — the design's own
 * fallback string (`Fictiv Platform.dc.html:9757`). Exported so the collapsed
 * header's test can assert against one constant rather than a copied literal. */
export const REQUEST_DETAILS_RECAP_FALLBACK = 'Need-by date, region, add-on services & notes'

/**
 * UID-359 — the collapsed "Request details & services" subtitle: a LIVE recap of
 * what the customer has actually set, falling back to the generic prompt when
 * they've set nothing.
 *
 * Mirrors the design's `reqRecapParts`/`reqSummary`
 * (`Fictiv Platform.dc.html:9721`/`:9757`) exactly, including its two
 * deliberate omissions: `region` contributes ONLY when it isn't the `any`
 * default (naming "No preference" as a thing you chose is noise), and the notes
 * body is never echoed — just the fact that notes exist. Blank-but-present
 * notes (`'   '`) count as absent, same as the design's `req.note.trim()`.
 */
export function requestDetailsRecap({
  needByDate,
  region,
  notes,
  servicesCount,
}: {
  readonly needByDate: string
  readonly region: RfqRegion
  readonly notes: string
  readonly servicesCount: number
}): string {
  const parts: string[] = []
  if (needByDate) parts.push(`Need-by ${needByDate}`)
  if (region !== 'any') {
    const label = REGION_OPTIONS.find((option) => option.value === region)?.label
    if (label) parts.push(label)
  }
  if (servicesCount > 0) {
    parts.push(`${servicesCount} service${servicesCount === 1 ? '' : 's'}`)
  }
  if (notes.trim() !== '') parts.push('Notes added')
  return parts.length > 0 ? parts.join(' · ') : REQUEST_DETAILS_RECAP_FALLBACK
}

/** Client-side "past date" guard (screens.md §15's implied validation —
 * ADR-0042 notes a past date is a UX concern, not a contract one, so the
 * server never rejects it; this closes that gap). `todayIsoDate` is passed
 * in rather than read from `Date.now()` internally so this stays pure and
 * deterministic under test. */
export function validateNeedByDate(needByDate: string, todayIsoDate: string): string | undefined {
  if (!needByDate) return undefined
  return needByDate < todayIsoDate ? "Need-by date can't be in the past." : undefined
}

export interface ReviewFormState {
  readonly lines: readonly RfqReviewLine[]
  readonly needByDate: string
  readonly region: RfqRegion
  readonly notes: string
  readonly services: readonly RfqReviewService[]
  readonly noServicesAck: boolean
  // ── UID-117 shipping pre-selection (ADR-0053), refined by UID-201 (ADR-0076) —
  //    OPTIONAL so existing callers compile unchanged; 'none' / '' both mean
  //    "omit on the wire". ──
  readonly shipMethod?: RfqShipMethod | 'none'
  // UID-201 (ADR-0076): own-account now REFERENCES a saved shipping-account book id
  // (resolve-on-write server-side), REPLACING the retired free-text carrier-account input.
  readonly shippingAccountId?: string
  readonly shipToAddressId?: string
}

/** Client-side own-account guard (UID-201, ADR-0076 Decision 1 — refines the
 * UID-117/ADR-0053 §4 free-text rule): choosing `own_account` requires SELECTING a
 * saved shipping account (`shippingAccountId`), not typing a free-text number. Mirrors
 * `validateNeedByDate`'s pure, message-returning shape; the server enforces the same
 * requirement (resolve-on-write + IDOR gate) but this closes the gap at the edge so the
 * customer never submits a body the server rejects. */
export function validateShipping(
  shipMethod: RfqShipMethod | 'none' | undefined,
  shippingAccountId: string,
): string | undefined {
  if (shipMethod === 'own_account' && shippingAccountId.trim() === '') {
    return 'Select a shipping account for own-account shipping.'
  }
  return undefined
}

export type RfqSubmitBlocker = 'no_parts'

export interface RfqSubmitReadinessInput {
  readonly partsCount: number
}

/** The RFQ review screen's submit-readiness gate. Per current product
 * direction, need-by date and requested services are OPTIONAL,
 * informational-only fields and no longer gate submission — the ONLY
 * remaining requirement is "at least one part" (part-configuration
 * completeness is tracked separately via `needsConfigureLineIds` and is
 * enforced by the callers of this function, not folded in here). Returns
 * every currently-unmet requirement, in a fixed order, so the summary rail
 * can list all of them at once rather than one at a time. */
export function rfqSubmitBlockers(input: RfqSubmitReadinessInput): readonly RfqSubmitBlocker[] {
  const blockers: RfqSubmitBlocker[] = []
  if (input.partsCount === 0) blockers.push('no_parts')
  return blockers
}

/** `true` once every submit-readiness requirement is met — i.e. `rfqSubmitBlockers`
 * returns no blockers. */
export function canSubmitRfq(input: RfqSubmitReadinessInput): boolean {
  return rfqSubmitBlockers(input).length === 0
}

/** Maps the review screen's local `RfqReviewService` list onto the wire's
 * `RfqRequestedService[]` shape — drops the local-only `localId`, trims
 * `description` (omitting it when blank), and only carries `sourceableIds`
 * for `specific_parts` scope. Shared by `buildCreateRequest` and
 * `buildUpdateRequest` (UID-242) so the two mappers can never drift on how a
 * service is put on the wire. */
function mapRequestedServices(services: readonly RfqReviewService[]): RfqRequestedService[] {
  return services.map((service) => {
    const trimmedDescription = service.description.trim()
    return {
      type: service.type,
      scope: service.scope,
      ...(trimmedDescription ? { description: trimmedDescription } : {}),
      ...(service.scope === 'specific_parts' ? { sourceableIds: [...service.sourceableIds] } : {}),
    }
  })
}

/** Maps one local `RfqReviewLine` onto the wire's `RfqLineRequest` (ADR-0085) —
 * the shared per-line mapping both `buildCreateRequest` and `buildUpdateRequest` use
 * so a `specSelection` (once set by the Configure step) can never drift between the
 * two write paths. `specSelection` is OMITTED (not sent as `null`) while the line
 * still needs Configure — `RfqLineRequest.specSelection` is optional-on-write, and
 * omitting keeps a not-yet-configured line's wire body identical to what it was
 * before UID-260 (no behavior change for every existing caller/test).
 *
 * UID-346 (ADR-0117 D5) — only the buyer-authored `specification` is ever sent.
 * The deprecated `attributes` bag is a SERVER-DERIVED READ projection now — a
 * value supplied on write is ignored by the server, so sending it here would be
 * dead weight at best and misleading at worst; this mapper never writes it.
 * `unitPriceAmount`/`currency`/`indicativeUnitPrice`/`pricingOutcome` are all
 * server-derived and are never trusted from the client even if a stale value is
 * sitting in local state (UID-260, ADR-0085 D1; UID-346, ADR-0117 D7). */
function buildLineRequest(line: RfqReviewLine): RfqLineRequest {
  const base: LineItem = {
    status: 'pending',
    sourceable: line.sourceable,
    quantity: { value: line.quantity, uom: line.sourceable.uom.saleUom },
  }
  // UID-346 (ADR-0117 D5) — gate on `specification`, NOT on the wrapper. A
  // truthiness check on the wrapper is the exact defect D5 exists to remove,
  // and here it had teeth: a wrapper without a `specification` emitted
  // `specSelection: { specification: undefined }`, which `JSON.stringify`
  // collapses to `{}` — rejected by ajv (`SpecSelection` requires
  // `specification`, `additionalProperties: false`). Submit is gated by
  // `needsConfigure` so it never reached the wire; Share is NOT gated, so it
  // surfaced as an unexplained 400. The persisted-schema bump to v2 removes
  // the producer, and this uses the same predicate as `needsConfigure` so the
  // two can never disagree again.
  if (!line.specSelection?.specification) return base
  return { ...base, specSelection: { specification: line.specSelection.specification } }
}

/** Maps the review screen's local form state onto the `POST /rfq` wire body
 * (ADR-0042). Most optional fields are OMITTED rather than sent empty/null
 * when the user left them blank — matches `RfqCreateRequest`'s absent-safe
 * contract and keeps the request minimal. `region` is the one exception
 * (ADR-0070): the form always holds a concrete `RfqRegion` member (`any` is
 * the default, not an absent state), so it is always emitted — there is no
 * more omit-when-"no preference" branch. `noServicesAck` is intentionally
 * NOT mapped onto the wire body — it is a client-only submit-readiness gate
 * (see `rfqSubmitBlockers`), not contract data. */
export function buildCreateRequest(state: ReviewFormState): RfqCreateRequest {
  const trimmedNotes = state.notes.trim()
  const lines: RfqLineRequest[] = state.lines.map((line) => buildLineRequest(line))

  // UID-117 shipping (ADR-0053), refined by UID-201 (ADR-0076), omit-when-empty like
  // region/notes:
  //  - 'none'/undefined method → omit everything (the placeholder maps to omit-from-wire);
  //  - shippingAccountId is emitted ONLY for own_account (the server 400s it otherwise via
  //    the mirror cross-field guard), and only when non-empty — the compose write path no
  //    longer sends the DEPRECATED free-text shipAccountNumber at all (ADR-0076 Decision 1);
  //  - shipToAddressId is emitted whenever a saved ship-to was chosen.
  const shipMethod = state.shipMethod
  const trimmedShippingAccountId = (state.shippingAccountId ?? '').trim()
  const trimmedShipToAddressId = (state.shipToAddressId ?? '').trim()

  return {
    lines,
    ...(state.needByDate ? { needByDate: state.needByDate } : {}),
    region: state.region,
    ...(trimmedNotes ? { notes: trimmedNotes } : {}),
    ...(shipMethod && shipMethod !== 'none' ? { shipMethod } : {}),
    ...(shipMethod === 'own_account' && trimmedShippingAccountId
      ? { shippingAccountId: trimmedShippingAccountId }
      : {}),
    ...(trimmedShipToAddressId ? { shipToAddressId: trimmedShipToAddressId } : {}),
    ...(state.services.length > 0
      ? { requestedServices: mapRequestedServices(state.services) }
      : {}),
  }
}

/** Maps the review screen's local form state onto the `PATCH /rfq/{rfqId}` wire body
 * (ADR-0082, UID-242 Item 1) — the eager-draft PATCH-sync fired at Share and at Submit.
 *
 * UID-242 Follow-up 1 (data-loss fix): unlike `buildCreateRequest` (where "absent" and
 * "blank" both mean "never set" — there is nothing to distinguish), a PATCH's absent vs
 * `null` are DISTINCT per `RfqUpdateRequest` (openapi.yaml): absent means "leave the
 * server's existing value untouched," `null` means "clear it." The previous version of
 * this mapper reused `buildCreateRequest`'s omit-when-blank convention, so a user who
 * cleared a previously-set need-by date / notes / services could never propagate that
 * clear — the PATCH simply never mentioned the field, and the stale server value silently
 * survived. `needByDate`/`notes`/`requestedServices` are therefore now ALWAYS present on
 * this mapper's output: blank → `null` (needByDate/notes) or `[]` (requestedServices —
 * `[]` and `null` are observably identical to a reading client per ADR-0082's
 * `RfqUpdateRequest.requestedServices` doc: both flatten to "no services" on the read
 * wire, so `[]` — the form's natural current-selection representation — is the chosen
 * cleared form here), filled → the value. `region` was already always-sent (ADR-0070's
 * "always a concrete member, never absent" default) and needs no change.
 *
 * `shipMethod`/`shippingAccountId`/`shipToAddressId` are deliberately UNCHANGED
 * (still omitted when blank) — out of this follow-up's scope; only the four fields named
 * above (needByDate, notes, requestedServices, and region, already-correct) are covered
 * here. */
export function buildUpdateRequest(state: ReviewFormState): RfqUpdateRequest {
  const trimmedNotes = state.notes.trim()
  const lines: RfqLineRequest[] = state.lines.map((line) => buildLineRequest(line))

  const shipMethod = state.shipMethod
  const trimmedShippingAccountId = (state.shippingAccountId ?? '').trim()
  const trimmedShipToAddressId = (state.shipToAddressId ?? '').trim()

  return {
    lines,
    needByDate: state.needByDate ? state.needByDate : null,
    region: state.region,
    notes: trimmedNotes ? trimmedNotes : null,
    requestedServices: mapRequestedServices(state.services),
    ...(shipMethod && shipMethod !== 'none' ? { shipMethod } : {}),
    ...(shipMethod === 'own_account' && trimmedShippingAccountId
      ? { shippingAccountId: trimmedShippingAccountId }
      : {}),
    ...(trimmedShipToAddressId ? { shipToAddressId: trimmedShipToAddressId } : {}),
  }
}

/**
 * UID-265b (ADR-0093 D2/D4) — the wire body for `POST /rfq/{rfqId}/lines`,
 * the post-submit APPEND.
 *
 * Deliberately built from the SAME two functions the create/PATCH paths use
 * (`toReviewLine` → `buildLineRequest`), so an appended line's shape can
 * never drift from a composed one — in particular `specSelection` is omitted
 * (never sent as `null`) for a not-yet-configured line, and
 * `unitPriceAmount`/`currency` are never transmitted at all (ADR-0085 D1's
 * trust boundary: pricing is server-derived on the append path too).
 *
 * `lines` here is ONLY what is being added. It is never the RFQ's existing
 * set: `PATCH`'s `lines` is a full replacement whose compare-and-set is on
 * `status` alone (ADR-0093 F7), so transmitting a possibly-stale full array
 * against a live RFQ is an unguarded lost update. An append removes that
 * class by construction — there is no version to be stale about.
 */
export function buildAppendLinesRequest(
  selected: readonly Sourceable[],
  sessionAccountId: string,
): RfqLineAppendRequest {
  return {
    lines: selected.map((sourceable) =>
      buildLineRequest(toReviewLine(sourceable, sessionAccountId)),
    ),
  }
}

/**
 * UID-346 (ADR-0117 D6b/D9, 2026-08-27 follow-up) — the same `POST
 * /rfq/{rfqId}/lines` wire body as {@link buildAppendLinesRequest}, but for a
 * caller that already holds composed `RfqReviewLine` rows (the compose
 * screen's own `lines`) rather than raw `Sourceable`s. `RfqReviewPage`'s
 * Configure save uses this to append any locally-added-but-not-yet-synced
 * line to the server draft — set-based, append-only (ADR-0093 D2: "the
 * client transmits only what it is adding"), never a full-array `PATCH` —
 * immediately before the real `bulk-configure` call, so a part added to the
 * local bundle AFTER the eager 0→1 draft create (which only the first part
 * rides) genuinely exists as a line on the server before being named in that
 * call. Reuses {@link buildLineRequest} directly — no second mapper, so an
 * appended line's shape can never drift from a PATCHed/created one.
 */
export function buildAppendLinesRequestFromReviewLines(
  linesToAppend: readonly RfqReviewLine[],
): RfqLineAppendRequest {
  return { lines: linesToAppend.map((line) => buildLineRequest(line)) }
}

/**
 * UID-114 "Move to new RFQ" — the row menu's real (non-stub) behavior:
 * builds a wire body for a SEPARATE, minimal RFQ containing only the one
 * moved line, with no other metadata carried over from the request it's
 * leaving. Deliberately does not reuse the current draft's `needByDate`/
 * `region`/`notes`/`services` — those describe the ORIGINAL request, not
 * this one-line spinoff. `region` lands on the plain default `any` (ADR-0070)
 * rather than carrying over whatever the original draft had selected — same
 * "no metadata carried over" intent, just via the explicit default member
 * instead of an omitted/sentinel value. `RfqCreateRequest.lines` is the only
 * required field on the wire (`openapi.yaml` — `needByDate` etc. are all
 * optional), so a minimal single-line body plus the default region is a
 * fully valid, real create request, not a half-filled one.
 *
 * UID-262 (ADR-0088): the caller (`RfqReviewPage.handleMoveToNewRfq`) creates
 * this as a *draft* and does NOT submit it — it then navigates the user
 * straight to the new draft's review page (`/rfq/{id}`), matching the design
 * source of truth (`.dc.html` `confirmMoveRfqNew`). The earlier UID-114
 * behavior additionally `POST`ed `/rfq/{rfqId}/submit` (producing a
 * *requested* RFQ) on the theory that an unsubmitted second draft was
 * unreachable; that theory no longer holds now that the move navigates
 * directly to the new draft, so the submit step was removed.
 */
export function buildMoveToNewRfqRequest(line: RfqReviewLine): RfqCreateRequest {
  return buildCreateRequest({
    lines: [line],
    needByDate: '',
    region: 'any',
    notes: '',
    services: [],
    noServicesAck: true,
    // UID-117 (ADR-0053) / UID-201 (ADR-0076): a spun-off single line carries no
    // shipping — empty, per the ADR-0050 minimal-body precedent (all shipping fields
    // omitted; own-account references nothing).
    shipMethod: 'none',
    shippingAccountId: '',
    shipToAddressId: '',
  })
}

/* -------------------------------------------------------------------------- *
 * UID-333 D1 (ADR-0116) — the Requirements column's spec labels
 * -------------------------------------------------------------------------- */

/** The Requirements cell's two lines for one compose-screen row. */
export interface RequirementsLabels {
  /** Bold primary label — the configured process. `null` ⇒ not configured. */
  readonly process: string | null
  /** Sub-line: `material · finish`, either part omitted. `null` ⇒ neither. */
  readonly detail: string | null
}

/**
 * The design's Requirements cell content for a compose-screen row: the process
 * as a bold primary label, then a `·`-joined sub-line of the chosen material
 * and finish (`dc.html:4160`'s `Requirements` column — e.g. "Urethane casting"
 * over "Silicone 40A · Matte").
 *
 * MIGRATED by UID-346 (ADR-0117 D5) from the untyped `attributes` bag to the
 * typed `specification`. Three things made that the right move rather than a
 * refactor:
 *
 *  1. **It fixes a real display bug.** `specification.process` is a
 *     `PartProcess` enum token, and the server's deprecated `attributes`
 *     projection copies it verbatim (`deriveSpecAttributes`, `routes.ts:96`).
 *     Reading the bag therefore rendered "cnc_machining" — a raw token — in
 *     the design's most informative cell on every newly-configured line.
 *     `formatProcessLabel` resolves it through the app's single source of
 *     process labels (ADR-0117 D1 forbids a second, hand-typed list).
 *  2. **`attributes` is deprecated and server-DERIVED**, one-directionally,
 *     from `specification`. Reading the projection instead of the source was
 *     always a layer too far out; the schema states removal is a follow-up
 *     "once the last reader is gone" — this accessor WAS that last reader, so
 *     the bag can now be deleted without touching this path.
 *  3. **It removes a whole class of fragility.** The bag needed a
 *     case-insensitive index and a declared key-priority list because its keys
 *     were untyped and their spelling could drift (the server projects
 *     lowercase; the pre-UID-346 client wrote `Process`/`Material`/`Finish`).
 *     A typed field has none of that surface.
 *
 * Returns `{ process: null, detail: null }` when there is no `specification`,
 * so the caller renders its "needs configuration" hint. It deliberately never
 * returns a dash: the SKU column this replaced rendered `—` for every custom
 * line, which is the defect ADR-0116 D1 exists to remove, not a convention to
 * carry over.
 *
 * The design's example sub-line also shows a tolerance. **This contract has no
 * tolerance field** — `RfqLineSpecification` carries process/material/finish
 * plus inspections/certificates — so that segment is knowingly absent rather
 * than fabricated (ADR-0116 D1).
 */
export function requirementsLabelsForLine(line: RfqReviewLine): RequirementsLabels {
  const specification = line.specSelection?.specification
  if (!specification) return { process: null, detail: null }

  // Gate-5 W1 — validate at the edge, then TRUST past it. An earlier pass kept
  // `.trim()` guards here, justified by "a client-authored draft reaches this
  // accessor before any POST". That justification does not survive: the only
  // two producers of a `specSelection` are the server read wire and a
  // `ConfigurePartsModal` save gated on `cascade.ready`, and the persisted
  // snapshot is now version-gated (v2) so a stale incompatible one is
  // discarded rather than rehydrated. Every required field of a
  // `RfqLineSpecification` is therefore non-empty by the time it gets here.
  //
  // Keeping the guards while ALSO deleting the label-less fallback in
  // `RequirementsCell` was incoherent — it assumed the data could be malformed
  // and simultaneously that it could not, and that specific combination is the
  // one that renders an empty cell with an orphaned edit pencil. One posture,
  // stated once: the contract holds past the edge.
  //
  // `finish: null` is a first-class value ("standard, no secondary
  // operation"), so it is omitted from the sub-line rather than rendered.
  const process = formatProcessLabel(specification.process)
  // The `!== ''` here is a JOIN concern, not distrust — it is what stops an
  // orphaned `·` separator if either value is empty. `process` needs no
  // equivalent because it is rendered standalone, not joined. Stated so the
  // deleted trim guards above don't look like they're creeping back in.
  const detail = [specification.material, specification.finish]
    .filter((part) => part !== null && part !== '')
    .join(' · ')
  return { process, detail: detail === '' ? null : detail }
}

/**
 * How many lines still need a real quote — the count behind the rail's
 * lavender "Custom quote  N part(s)" row (UID-333 F5, ADR-0116).
 *
 * Gate-5 fix. That row was first derived in the rail as
 * `partsCount - partProductionCount`, which OVER-counts.
 * `partProductionLineCount` is the set `partProductionSubtotal` sums — priced
 * CATALOG lines only — so its complement wrongly swept in two populations:
 *
 *  - a catalog line whose `indicativePrice` is missing or unparseable. It is a
 *    catalog part: `needsConfigure` is false for it, Configure never applies,
 *    it cannot be quoted — yet it was reported as needing a custom quote while
 *    its own row rendered an add-to-cart button.
 *  - an auto-priced custom candidate (`isAutoPricedCustomCandidate`) showing a
 *    live preview price range. The design classes this as `auto-configured`,
 *    which is `cart: true` — priced, not awaiting a quote (`dc.html:7688-7693`).
 *
 * `partProductionCount` remains correct for the MONEY row, where the label must
 * match the sum (UID-309 Gate-5 S4) — it is simply the wrong basis for a
 * POPULATION count. This helper matches the design's `cart: false` set
 * (`needs-config` + `needs-rfq`) directly instead of deriving it by subtraction:
 * a line needs a quote when it is neither a catalog line nor auto-priced.
 */
export function needsQuoteLineCount(lines: readonly RfqReviewLine[]): number {
  return lines.filter((line) => !isCatalogLine(line) && !isAutoPricedCustomCandidate(line)).length
}
